import { useState, useEffect, useCallback } from 'react';
import type { TicketTypeTemplate } from '../types/ticketTypeTemplate';
import {
  getTicketTypeTemplates,
  refreshTicketTypeTemplatesCache,
  invalidateTicketTypeTemplatesCache,
} from '../services/templateCacheService';

// Registers the client-side pricing rule functions into PRICING_RULE_REGISTRY
// and field-default resolvers into FIELD_DEFAULT_RESOLVER_REGISTRY
import '../data/pricingRules';
import '../data/onsiteServiceAreas';

/**
 * Load the ticket-type-templates catalog. When `customerId` is supplied,
 * the API merges that customer's active overrides — line items and
 * additional fields — into the matching templates server-side.
 */
export function useTicketTypeTemplates(customerId?: string | null) {
  const [templates, setTemplates] = useState<TicketTypeTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isStale, setIsStale] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Which customer the current `templates` were merged for (null = the global
  // catalog). `isLoading` alone can't answer that: on the render where the
  // customer changes, the previous customer's templates are still here and
  // the load has not started. A caller that needs the overrides applied (the
  // linked-ticket prefill) waits on this instead.
  const [loadedForCustomerId, setLoadedForCustomerId] = useState<string | null>(null);

  const load = useCallback(async (signal?: AbortSignal) => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await getTicketTypeTemplates(
        customerId ? { customerId } : undefined,
      );
      if (signal?.aborted) return;
      setTemplates(result.items);
      setLoadedForCustomerId(customerId ?? null);
      setIsStale(result.isStale ?? false);
      if (result.warning) {
        console.warn('[useTicketTypeTemplates]', result.warning);
      }
    } catch (err: any) {
      if (err.name === 'AbortError') return;
      if (signal?.aborted) return;
      console.error('[useTicketTypeTemplates] ERROR:', err.message);
      setError(err.message || 'Failed to load ticket type templates');
    } finally {
      if (!signal?.aborted) {
        setIsLoading(false);
      }
    }
  }, [customerId]);

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [load]);

  const retry = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await getTicketTypeTemplates(
        customerId
          ? { customerId, forceRefresh: true }
          : { forceRefresh: true },
      );
      setTemplates(result.items);
      setIsStale(false);
    } catch (err: any) {
      setError(err.message || 'Failed to load ticket type templates');
    } finally {
      setIsLoading(false);
    }
  }, [customerId]);

  const refresh = useCallback(async () => {
    setIsLoading(true);
    const result = await refreshTicketTypeTemplatesCache();
    if (result.success && result.items) {
      setTemplates(result.items);
      setIsStale(false);
      setError(null);
    } else if (result.error) {
      setError(result.error);
    }
    setIsLoading(false);
  }, []);

  const invalidate = useCallback(async () => {
    setIsLoading(true);
    const result = await invalidateTicketTypeTemplatesCache();
    setTemplates([]);
    setIsStale(false);
    setError(null);

    // Re-fetch fresh data now that both client + server caches are cleared
    try {
      const fresh = await getTicketTypeTemplates({ forceRefresh: true });
      setTemplates(fresh.items);
    } catch (err: any) {
      setError(err.message || 'Failed to reload ticket type templates');
    } finally {
      setIsLoading(false);
    }

    return result;
  }, []);

  return { templates, loadedForCustomerId, isLoading, isStale, error, retry, refresh, invalidate };
}

export function getAllGroupsFromTemplates(templates: TicketTypeTemplate[]): string[] {
  const groups = new Set<string>();
  templates.forEach(t => groups.add(t.group));
  return Array.from(groups).sort();
}
